import { useContext, useRef, useState } from "react";
import { Dialog, DialogPanel, DialogTitle, Description } from "@headlessui/react";
import { FaFileExcel } from "react-icons/fa";
import { DarkModeContext } from "../Context/DarkModeContext";

const loadExcel = async () => {
  const module = await import("xlsx");
  return module.read ? module : module.default;
};
const aliases = {
  title: ["title", "name", "videotitle", "materialtitle", "videoname", "materialname"],
  description: ["description", "desc", "videodescription", "materialdescription"],
  url: ["url", "link", "videourl", "materialurl", "videolink", "materiallink"],
};
const normalize = (value) => String(value ?? "").trim().toLowerCase().replace(/[\s_-]/g, "");
export default function ExcelContentImport({ type, courseId, courseName, onImport, onOpen, onClose, disabled = false, className, label = "Import from Excel", role }) {
  const { isDarkMode } = useContext(DarkModeContext);
  const [open, setOpen] = useState(false);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState("");
  const [workbook, setWorkbook] = useState(null);
  const [sheetName, setSheetName] = useState("");
  const fileRef = useRef(null);
  const operation = useRef(0);
  const plural = type === "video" ? "videos" : "materials";
  const instructions = "Use Title, Description, and URL columns, with one item per row. You can review and edit every item before saving.";
  const close = () => { operation.current += 1; setOpen(false); setReading(false); onClose?.(); };

  const chooseFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const request = ++operation.current;
    setError("");
    setWorkbook(null);
    setSheetName("");
    if (!/\.(xlsx|xls|csv)$/i.test(file.name)) { setError("Choose an .xlsx, .xls, or .csv file."); return; }
    if (file.size > 10 * 1024 * 1024) { setError("Please choose a file smaller than 10 MB."); return; }
    setReading(true);
    try {
      const XLSX = await loadExcel();
      const book = XLSX.read(await file.arrayBuffer(), { type: "array", sheetRows: 1002 });
      if (!book.SheetNames.length) throw new Error("The workbook has no worksheets.");
      if (request !== operation.current) return;
      setWorkbook(book);
      setSheetName(book.SheetNames[0]);
    } catch (err) {
      if (request === operation.current) setError(err.message || "Could not read this spreadsheet.");
    } finally {
      if (request === operation.current) setReading(false);
    }
  };

  const review = async () => {
    if (!workbook || reading) return;
    setReading(true);
    setError("");
    const request = ++operation.current;
    try {
      const XLSX = await loadExcel();
      const sheet = workbook.Sheets[sheetName];
      const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: false, blankrows: true });
      const headerRow = matrix.findIndex((row) => row.some((cell) => String(cell).trim()));
      if (headerRow < 0) throw new Error("This worksheet is empty.");
      const headers = matrix[headerRow].map(normalize);
      const columns = Object.fromEntries(Object.entries(aliases).map(([key, names]) => [key, headers.findIndex((header) => names.includes(header))]));
      const missing = Object.keys(columns).filter((key) => columns[key] < 0);
      if (missing.length) throw new Error("Missing columns: " + missing.join(", ") + ". Use Title, Description, and URL in the header row.");
      const startRow = XLSX.utils.decode_range(sheet["!ref"] || "A1").s.r;
      const startColumn = XLSX.utils.decode_range(sheet["!ref"] || "A1").s.c;
      const imported = matrix.slice(headerRow + 1).map((row, index) => {
        const item = Object.fromEntries(Object.entries(columns).map(([key, column]) => [key, String(row[column] ?? "").trim()]));
        for (const key of Object.keys(columns).filter((key) => /url$/i.test(key) && columns[key] >= 0)) {
          const address = XLSX.utils.encode_cell({ r: startRow + headerRow + 1 + index, c: startColumn + columns[key] });
          item[key] = sheet[address]?.l?.Target || item[key];
        }
        return item;
      }).filter((row) => Object.values(row).some(Boolean));
      if (!imported.length) throw new Error("No content rows were found below the headers.");
      const range = XLSX.utils.decode_range(sheet["!fullref"] || sheet["!ref"] || "A1");
      if (imported.length > 1000 || range.e.r - startRow - headerRow > 1000) throw new Error("Import up to 1,000 rows at a time. Split this worksheet into smaller files.");
      if (request !== operation.current) return;
      onImport(imported);
      setOpen(false);
    } catch (err) {
      if (request === operation.current) setError(err.message || "Could not import this worksheet.");
    } finally {
      if (request === operation.current) setReading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const XLSX = await loadExcel();
      const book = XLSX.utils.book_new();
      const sheet = XLSX.utils.aoa_to_sheet([["Title", "Description", "URL"]]);
      sheet["!cols"] = [{ wch: 30 }, { wch: 60 }, { wch: 60 }];
      XLSX.utils.book_append_sheet(book, sheet, type === "video" ? "Videos" : "Materials");
      XLSX.writeFile(book, plural + "-import-template.xlsx");
    } catch { setError("Could not download the template. Please try again."); }
  };

  return <>
    <button type="button" disabled={disabled} role={role} className={className || "flex items-center justify-center gap-2 rounded-lg bg-emerald-700 px-4 py-2 text-white hover:bg-emerald-800"} onClick={() => { setError(""); setWorkbook(null); setSheetName(""); setOpen(true); onOpen?.(); }}><FaFileExcel aria-hidden="true" />{label}</button>
    <Dialog open={open} onClose={close} className="relative z-[70]">
      <div className="fixed inset-0 bg-slate-950/65 backdrop-blur-sm" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center overflow-y-auto p-4">
        <DialogPanel className={`max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto rounded-2xl border p-6 shadow-2xl ${isDarkMode ? "border-white/10 bg-[#191919] text-white" : "border-slate-200 bg-white text-slate-900"}`}>
          <DialogTitle className="text-xl font-bold">Import {plural} from Excel</DialogTitle>
          <Description className="mt-2 text-sm text-slate-500">{instructions}</Description>
          <button type="button" onClick={downloadTemplate} className="mt-4 text-sm font-semibold text-blue-500 underline">Download Excel template</button>
          <label className="mt-5 block text-sm font-semibold" htmlFor={`excel-${type}-${courseId || "all"}`}>Spreadsheet file</label>
          <input ref={fileRef} id={`excel-${type}-${courseId || "all"}`} type="file" accept=".xlsx,.xls,.csv" onChange={chooseFile} disabled={reading} className="mt-2 block w-full min-w-0 rounded-xl border border-slate-400/30 p-3 text-sm" />
          {workbook && <label className="mt-4 block text-sm font-semibold">Worksheet<select value={sheetName} onChange={(event) => { setSheetName(event.target.value); setError(""); }} disabled={reading} className="mt-2 w-full rounded-xl border border-slate-300 bg-white p-3 text-slate-900">{workbook.SheetNames.map((name) => <option key={name}>{name}</option>)}</select></label>}
          {reading && <p role="status" className="mt-3 text-sm">Reading spreadsheet...</p>}
          {error && <p role="alert" className="mt-3 text-sm text-red-500">{error}</p>}
          <div className="mt-6 flex justify-end gap-3"><button type="button" onClick={close} className="rounded-xl border border-slate-400/30 px-4 py-3 text-sm">Cancel</button><button type="button" disabled={!workbook || reading} onClick={review} className="rounded-xl bg-emerald-700 px-5 py-3 text-sm font-semibold text-white disabled:opacity-50">Review import</button></div>
        </DialogPanel>
      </div>
    </Dialog>
  </>;
}
